'use client'

import { IBM_Plex_Sans } from 'next/font/google'
import type {} from '@mui/x-data-grid/themeAugmentation'
import type {} from '@mui/material/themeCssVarsAugmentation'
import { createTheme } from '@mui/material/styles'

import SelectArrowDownIcon from '@/components/global/forms/SelectArrowDownIcon'
import { DEFAULT_DATA_GRID_LOCALE_TEXT } from '../dataGridLocaleText'
import { lightPalette, darkPalette } from './palette'
import type {
  BorderPalette,
  ChatPalette,
  ColorScale,
  SimpleSemanticColor,
  StatusPalette,
} from './types'

const ibmPlexSans = IBM_Plex_Sans({
  weight: ['300', '400', '500', '600', '700'],
  subsets: ['latin'],
  display: 'swap',
})

const bodyEmphasisTypography = {
  fontSize: '16px',
  lineHeight: '24px',
  fontWeight: 500,
}

const bodySmTypography = {
  fontSize: '14px',
  lineHeight: '20px',
  fontWeight: 400,
}

const buttonBaseStyles = {
  borderRadius: '8px',
  textTransform: 'none' as const,
  fontSize: '14px',
  lineHeight: '20px',
  fontWeight: 500,
  letterSpacing: 0,
  gap: '8px',
  boxShadow: 'none',
  '&:hover': {
    boxShadow: 'none',
  },
}

const buttonStandardSizeStyles = {
  minWidth: '67px',
  minHeight: '40px',
  padding: '10px 16px',
}

const buttonSmallSizeStyles = {
  minWidth: '59px',
  minHeight: '32px',
  padding: '6px 12px',
}

const iconButtonBaseStyles = {
  borderRadius: '8px',
  gap: '8px',
}

const iconButtonMediumSizeStyles = {
  width: '40px',
  height: '40px',
  padding: '10px',
}

const iconButtonSmallSizeStyles = {
  width: '32px',
  height: '32px',
  padding: '6px',
}

const brand = {
  50: 'var(--mui-palette-brand-50)',
  100: 'var(--mui-palette-brand-100)',
  200: 'var(--mui-palette-brand-200)',
  300: 'var(--mui-palette-brand-300)',
  400: 'var(--mui-palette-brand-400)',
  500: 'var(--mui-palette-brand-500)',
  600: 'var(--mui-palette-brand-600)',
  700: 'var(--mui-palette-brand-700)',
  800: 'var(--mui-palette-brand-800)',
  900: 'var(--mui-palette-brand-900)',
} satisfies ColorScale

const neutral = {
  0: 'var(--mui-palette-neutral-0)',
  50: 'var(--mui-palette-neutral-50)',
  100: 'var(--mui-palette-neutral-100)',
  200: 'var(--mui-palette-neutral-200)',
  300: 'var(--mui-palette-neutral-300)',
  400: 'var(--mui-palette-neutral-400)',
  500: 'var(--mui-palette-neutral-500)',
  600: 'var(--mui-palette-neutral-600)',
  700: 'var(--mui-palette-neutral-700)',
  800: 'var(--mui-palette-neutral-800)',
  900: 'var(--mui-palette-neutral-900)',
} satisfies ColorScale

const red = {
  50: 'var(--mui-palette-red-50)',
  100: 'var(--mui-palette-red-100)',
  200: 'var(--mui-palette-red-200)',
  300: 'var(--mui-palette-red-300)',
  400: 'var(--mui-palette-red-400)',
  500: 'var(--mui-palette-red-500)',
  600: 'var(--mui-palette-red-600)',
  700: 'var(--mui-palette-red-700)',
  800: 'var(--mui-palette-red-800)',
  900: 'var(--mui-palette-red-900)',
} satisfies ColorScale

const border = {
  default: 'var(--mui-palette-border-default)',
  soft: 'var(--mui-palette-border-soft)',
  strong: 'var(--mui-palette-border-strong)',
} satisfies BorderPalette

const theme = createTheme({
  cssVariables: {
    colorSchemeSelector: 'class',
  },
  colorSchemes: {
    light: {
      palette: lightPalette,
    },
    dark: {
      palette: darkPalette,
    },
  },
  typography: {
    fontFamily: ibmPlexSans.style.fontFamily,
    allVariants: {
      lineHeight: 1,
    },
    titleMd: {
      fontSize: '24px',
      lineHeight: '32px',
      fontWeight: 600,
    },
    titleSm: {
      fontSize: '20px',
      lineHeight: '24px',
      fontWeight: 500,
    },
    body: {
      fontSize: '16px',
      lineHeight: '24px',
      fontWeight: 400,
    },
    bodyEmphasis: {
      ...bodyEmphasisTypography,
      display: 'block',
    },
    bodySupport: {
      fontSize: '16px',
      lineHeight: '24px',
      fontWeight: 400,
      fontStyle: 'italic',
    },
    bodySm: {
      ...bodySmTypography,
    },
    bodySmEmphasis: {
      fontSize: '14px',
      lineHeight: '20px',
      fontWeight: 500,
    },
    bodySmSupport: {
      fontSize: '14px',
      lineHeight: '20px',
      fontWeight: 400,
      fontStyle: 'italic',
    },
    bodySmEmphasisSupport: {
      fontSize: '14px',
      lineHeight: '20px',
      fontWeight: 500,
      fontStyle: 'italic',
    },
    caption: {
      fontSize: '12px',
      lineHeight: '16px',
      fontWeight: 400,
    },
    captionEmphasis: {
      fontSize: '12px',
      lineHeight: '16px',
      fontWeight: 500,
      letterSpacing: '1%',
    },
    captionSupport: {
      fontSize: '12px',
      lineHeight: '16px',
      fontWeight: 400,
      fontStyle: 'italic',
    },
  },
  components: {
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          bodyEmphasis: 'span',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        'form .MuiGrid-container:has(> .MuiButton-root + .MuiButton-root)': {
          gap: '12px',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          '&.MuiChip-sizeSmall': {
            height: 15,
            fontSize: '0.75rem',
            padding: '3px',
          },
        },
        label: {
          '&.MuiChip-labelSmall': {
            fontSize: '11px',
            fontWeight: 600,
            paddingLeft: '3px',
            paddingRight: '3px',
          },
        },
        avatar: {
          '&.MuiChip-avatarSmall': {
            width: 14,
            height: 14,
            fontSize: '0.675rem',
            marginLeft: 4,
            marginRight: -2,
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: neutral[0],
          backgroundImage: 'none',
          borderRight: `1px solid ${neutral[100]}`,
          color: neutral[900],
        },
      },
    },
    MuiAppBar: {
      defaultProps: {
        color: 'default',
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundColor: neutral[0],
          backgroundImage: 'none',
          color: neutral[900],
          borderBottom: `1px solid ${border.default}`,
          boxShadow: 'none',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '8px',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          ...bodyEmphasisTypography,
          color: neutral[900],
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          ...bodySmTypography,
          color: neutral[900],
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: 0,
          gap: '12px',
          '& > :not(style) ~ :not(style)': {
            marginLeft: 0,
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          minHeight: '36px',
          gap: '12px',
          borderRadius: '8px',
          padding: '8px 12px',
          '&:hover': {
            backgroundColor: neutral[100],
          },
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          padding: '8px',
        },
      },
    },

    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 20,
          color: neutral[900],
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          margin: 0,
        },
        primary: {
          ...bodySmTypography,
          color: neutral[900],
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'medium',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          minHeight: '40px',
          gap: '8px',
          alignItems: 'center',
          borderRadius: '8px',
          padding: '10px 8px',
          '&:has(.MuiChip-root)': {
            gap: '4px',
            minHeight: '40px',
            padding: '6px 8px !important',
          },
          '&:has(.MuiChip-root) .MuiChip-root': {
            height: 22,
            maxWidth: '100%',
          },
          '&:has(.MuiChip-root) .MuiChip-label': {
            paddingLeft: '8px',
            paddingRight: '8px',
            fontSize: '12px',
            lineHeight: '16px',
          },
          '&:has(.MuiChip-root) .MuiChip-deleteIcon': {
            width: 16,
            height: 16,
            fontSize: 16,
            marginLeft: '-4px',
            marginRight: '4px',
          },
          '& .MuiInputAdornment-root .MuiSvgIcon-root': {
            color: neutral[500],
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: neutral[200],
            borderWidth: '1px',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: neutral[500],
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: brand[400],
            borderWidth: '1px',
          },
        },
        input: {
          padding: 0,
        },
        sizeSmall: {
          minHeight: '36px',
          padding: '8px 12px',
          '& .MuiInputAdornment-root .MuiSvgIcon-root': {
            color: neutral[900],
          },
        },
        inputSizeSmall: {
          padding: 0,
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          borderRadius: '16px',
        },
        listbox: {
          padding: '8px',
        },
        option: {
          minHeight: '36px',
          gap: '8px',
          borderRadius: '8px',
          borderTop: '1px solid transparent',
          borderBottom: '1px solid transparent',
          backgroundClip: 'padding-box',
          padding: '8px 12px',
          '&[aria-selected="true"]': {
            backgroundColor: brand[50],
          },
          '&[aria-selected="true"].Mui-focused': {
            backgroundColor: brand[50],
          },
          '&.Mui-focused': {
            backgroundColor: neutral[100],
          },
        },
        groupLabel: {
          backgroundColor: 'inherit',
        },
        inputRoot: {
          minHeight: '40px',
          gap: '8px',
          padding: '10px 8px 10px 12px !important',
          '&:has(.MuiChip-root)': {
            gap: '4px',
            minHeight: '36px',
            padding: '6px 56px 6px 8px !important',
          },
          '& .MuiAutocomplete-input': {
            padding: '0 !important',
          },
          '& .MuiAutocomplete-endAdornment': {
            display: 'flex',
            alignItems: 'center',
            gap: '2px',
            right: 8,
          },
          '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator':
            {
              color: neutral[500],
              width: 24,
              height: 24,
              padding: 2,
            },
          '& .MuiAutocomplete-popupIndicator svg, & .MuiAutocomplete-clearIndicator svg':
            {
              fontSize: 20,
            },
          '&.MuiInputBase-sizeSmall': {
            minHeight: '36px',
            padding: '8px 12px !important',
            '&:has(.MuiChip-root)': {
              padding: '6px 56px 6px 8px !important',
            },
            '& .MuiAutocomplete-endAdornment': {
              display: 'flex',
              alignItems: 'center',
              gap: '2px',
              right: 12,
            },
            '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator':
              {
                color: neutral[900],
                width: 24,
                height: 24,
                padding: 2,
              },
          },
        },
        popupIndicator: {
          color: neutral[500],
        },
        clearIndicator: {
          color: neutral[500],
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        IconComponent: SelectArrowDownIcon,
        size: 'medium',
        MenuProps: {
          MenuListProps: {
            sx: {
              '& > .MuiMenuItem-root': {
                borderTop: '1px solid transparent',
                borderBottom: '1px solid transparent',
                backgroundClip: 'padding-box',
              },
            },
          },
          slotProps: {
            paper: {
              sx: {
                borderRadius: '16px',
              },
            },
            list: {
              sx: {
                padding: '8px',
                '& > .MuiMenuItem-root': {
                  borderTop: '1px solid transparent',
                  borderBottom: '1px solid transparent',
                  backgroundClip: 'padding-box',
                },
              },
            },
          },
        },
      },
      styleOverrides: {
        select: {
          minHeight: '20px',
          display: 'flex',
          alignItems: 'center',
          padding: '0 24px 0 4px !important',
          '&:has(.MuiChip-root)': {
            minHeight: '22px',
            padding: '0 24px 0 0 !important',
          },
          '&:has(.MuiChip-root) .MuiBox-root': {
            gap: '4px',
            minHeight: 22,
          },
        },
        icon: {
          color: neutral[500],
          fontSize: 20,
          right: 8,
        },
      },
      variants: [
        {
          props: { size: 'small' },
          style: {
            '& .MuiSelect-select': {
              padding: '0 24px 0 0 !important',
            },
            '& .MuiSelect-icon': {
              color: neutral[900],
              right: 12,
            },
          },
        },
      ],
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: '16px',
        },
        list: {
          padding: '8px',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          minHeight: '36px',
          gap: '8px',
          borderRadius: '8px',
          padding: '8px 12px',
          '&:hover': {
            backgroundColor: neutral[100],
          },
        },
      },
    },
    MuiRadio: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiCheckbox: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiSwitch: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiButton: {
      defaultProps: {
        size: 'small',
        disableElevation: true,
      },
      styleOverrides: {
        root: buttonBaseStyles,
        sizeMedium: buttonStandardSizeStyles,
        sizeSmall: buttonSmallSizeStyles,
      },
      variants: [
        {
          props: { variant: 'contained', color: 'primary' },
          style: {
            backgroundColor: brand[600],
            color: neutral[0],
            '&:hover': {
              backgroundColor: brand[500],
            },
            '&.Mui-disabled': {
              backgroundColor: brand[100],
              color: brand[300],
            },
          },
        },
        {
          props: { variant: 'contained', color: 'secondary' },
          style: {
            backgroundColor: brand[50],
            color: brand[600],
            '&:hover': {
              backgroundColor: brand[100],
            },
            '&.Mui-disabled': {
              backgroundColor: brand[50],
              color: brand[300],
            },
          },
        },
        {
          props: { variant: 'contained', color: 'error' },
          style: {
            backgroundColor: red[600],
            color: neutral[0],
            '&:hover': {
              backgroundColor: red[500],
            },
            '&.Mui-disabled': {
              backgroundColor: brand[100],
              color: neutral[400],
            },
          },
        },
        {
          props: { variant: 'soft', color: 'primary' },
          style: {
            backgroundColor: brand[50],
            color: brand[600],
            '&:hover': {
              backgroundColor: brand[100],
            },
            '&.Mui-disabled': {
              backgroundColor: brand[50],
              color: brand[300],
            },
          },
        },
        {
          props: { variant: 'contained', color: 'error' },
          style: {
            backgroundColor: red[600],
            color: neutral[0],
            '&:hover': {
              backgroundColor: red[500],
            },
            '&.Mui-disabled': {
              backgroundColor: red[50],
              color: red[300],
            },
          },
        },
        {
          props: { variant: 'soft', color: 'error' },
          style: {
            backgroundColor: red[50],
            color: red[600],
            '&:hover': {
              backgroundColor: red[100],
            },
            '&.Mui-disabled': {
              backgroundColor: red[50],
              color: red[300],
            },
          },
        },
        {
          props: { variant: 'outlined', color: 'primary' },
          style: {
            borderColor: neutral[300],
            color: neutral[900],
            backgroundColor: neutral[0],
            '&:hover': {
              borderColor: neutral[400],
              backgroundColor: neutral[0],
            },
            '&.Mui-disabled': {
              borderColor: neutral[200],
              color: neutral[300],
              backgroundColor: neutral[0],
            },
          },
        },
        {
          props: { variant: 'outlined', color: 'error' },
          style: {
            borderColor: red[300],
            color: red[600],
            backgroundColor: neutral[0],
            '&:hover': {
              borderColor: red[400],
              backgroundColor: red[50],
            },
            '&.Mui-disabled': {
              borderColor: red[100],
              color: red[300],
              backgroundColor: neutral[0],
            },
          },
        },
        {
          props: { variant: 'text', color: 'primary' },
          style: {
            color: neutral[900],
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: neutral[50],
            },
            '&.Mui-disabled': {
              color: neutral[300],
              backgroundColor: 'transparent',
            },
          },
        },
        {
          props: { variant: 'text', color: 'error' },
          style: {
            color: red[600],
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: red[50],
            },
            '&.Mui-disabled': {
              color: red[300],
              backgroundColor: 'transparent',
            },
          },
        },
      ],
    },
    MuiButtonGroup: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: 'none',
        },
        grouped: {
          minWidth: 'unset',
          borderRadius: 0,
          boxShadow: 'none',
        },
        firstButton: {
          borderLeftRadius: '8px',
        },
        lastButton: {
          borderRightRadius: '8px',
        },
      },
    },
    MuiIconButton: {
      defaultProps: {
        size: 'medium',
      },
      styleOverrides: {
        root: iconButtonBaseStyles,
        sizeMedium: iconButtonMediumSizeStyles,
        sizeSmall: iconButtonSmallSizeStyles,
      },
      variants: [
        {
          props: { variant: 'contained', color: 'primary' },
          style: {
            backgroundColor: brand[600],
            color: neutral[0],
            '&:hover': {
              backgroundColor: brand[500],
            },
            '&.Mui-disabled': {
              backgroundColor: brand[100],
              color: brand[300],
            },
          },
        },
        {
          props: { variant: 'soft', color: 'primary' },
          style: {
            backgroundColor: brand[50],
            color: brand[600],
            '&:hover': {
              backgroundColor: brand[100],
            },
            '&.Mui-disabled': {
              backgroundColor: brand[50],
              color: brand[300],
            },
          },
        },
        {
          props: { variant: 'soft', color: 'error' },
          style: {
            backgroundColor: red[50],
            color: red[600],
            '&:hover': {
              backgroundColor: red[100],
            },
            '&.Mui-disabled': {
              backgroundColor: brand[50],
              color: neutral[400],
            },
          },
        },
        {
          props: { variant: 'outlined', color: 'primary' },
          style: {
            backgroundColor: neutral[0],
            color: neutral[900],
            border: `1px solid ${neutral[200]}`,
            '&:hover': {
              backgroundColor: neutral[0],
              borderColor: neutral[400],
            },
            '&.Mui-disabled': {
              backgroundColor: neutral[0],
              borderColor: neutral[200],
              color: neutral[300],
            },
          },
        },
        {
          props: { variant: 'outlined', color: 'error' },
          style: {
            backgroundColor: neutral[0],
            color: red[600],
            border: `1px solid ${red[100]}`,
            '&:hover': {
              backgroundColor: neutral[0],
              borderColor: red[300],
            },
            '&.Mui-disabled': {
              backgroundColor: neutral[0],
              borderColor: neutral[200],
              color: neutral[400],
            },
          },
        },
        {
          props: { variant: 'text', color: 'primary' },
          style: {
            backgroundColor: 'transparent',
            color: neutral[900],
            '&:hover': {
              backgroundColor: neutral[100],
            },
            '&.Mui-disabled': {
              backgroundColor: 'transparent',
              color: neutral[300],
            },
          },
        },
        {
          props: { variant: 'text', color: 'error' },
          style: {
            backgroundColor: 'transparent',
            color: red[600],
            '&:hover': {
              backgroundColor: red[50],
            },
            '&.Mui-disabled': {
              backgroundColor: 'transparent',
              color: neutral[400],
            },
          },
        },
      ],
    },
    MuiDataGrid: {
      defaultProps: {
        localeText: DEFAULT_DATA_GRID_LOCALE_TEXT,
      },
      styleOverrides: {
        root: {
          '& .MuiDataGrid-row': {
            transition: 'background-color 120ms ease-in-out',
          },
          '& .MuiDataGrid-row:hover, & .MuiDataGrid-row.Mui-hovered': {
            backgroundColor:
              'color-mix(in srgb, var(--mui-palette-brand-600) 10%, transparent)',
          },
          '& .MuiDataGrid-row.Mui-selected:hover': {
            backgroundColor:
              'color-mix(in srgb, var(--mui-palette-brand-600) 18%, var(--mui-palette-action-selected))',
          },
          '.dark & .MuiDataGrid-row:hover, .dark & .MuiDataGrid-row.Mui-hovered':
            {
              backgroundColor:
                'color-mix(in srgb, var(--mui-palette-brand-600) 18%, transparent)',
            },
          '.dark & .MuiDataGrid-row.Mui-selected:hover': {
            backgroundColor:
              'color-mix(in srgb, var(--mui-palette-brand-600) 26%, var(--mui-palette-action-selected))',
          },
        },
      },
    },
    MuiInputLabel: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginTop: '2px',
          marginLeft: '12px',
          marginRight: 0,
          fontSize: '12px',
          lineHeight: '14px',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(101, 79, 161, 0.08)',
          },
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          fontSize: '12px',
          lineHeight: '16px',
          fontWeight: 500,
          letterSpacing: '1%',
          backgroundColor: brand[600],
          color: neutral[0],
          minWidth: 16,
          height: 16,
          borderRadius: 999,
          padding: '0 4px',
        },
      },
    },
  },
})

export default theme

declare module '@mui/material/styles' {
  interface Palette {
    brand: ColorScale
    neutral: ColorScale
    border: BorderPalette
    chat: ChatPalette
    status: StatusPalette
    whatsapp: SimpleSemanticColor
    red: ColorScale
  }

  interface PaletteOptions {
    brand?: ColorScale
    neutral?: ColorScale
    border?: BorderPalette
    chat?: ChatPalette
    status?: StatusPalette
    whatsapp?: SimpleSemanticColor
    red?: ColorScale
  }

  interface TypographyVariants {
    titleMd: React.CSSProperties
    titleSm: React.CSSProperties
    body: React.CSSProperties
    bodyEmphasis: React.CSSProperties
    bodySupport: React.CSSProperties
    bodySm: React.CSSProperties
    bodySmEmphasis: React.CSSProperties
    bodySmSupport: React.CSSProperties
    bodySmEmphasisSupport: React.CSSProperties
    captionEmphasis: React.CSSProperties
    captionSupport: React.CSSProperties
  }

  interface TypographyVariantsOptions {
    titleMd?: React.CSSProperties
    titleSm?: React.CSSProperties
    body?: React.CSSProperties
    bodyEmphasis?: React.CSSProperties
    bodySupport?: React.CSSProperties
    bodySm?: React.CSSProperties
    bodySmEmphasis?: React.CSSProperties
    bodySmSupport?: React.CSSProperties
    bodySmEmphasisSupport?: React.CSSProperties
    captionEmphasis?: React.CSSProperties
    captionSupport?: React.CSSProperties
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    titleMd: true
    titleSm: true
    body: true
    bodyEmphasis: true
    bodySupport: true
    bodySm: true
    bodySmEmphasis: true
    bodySmSupport: true
    bodySmEmphasisSupport: true
    captionEmphasis: true
    captionSupport: true
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    soft: true
  }
}

declare module '@mui/material/IconButton' {
  interface IconButtonPropsVariantOverrides {
    contained: true
    soft: true
    outlined: true
    text: true
  }

  interface IconButtonOwnProps {
    variant?: 'contained' | 'soft' | 'outlined' | 'text'
  }
}
